import type { Effect, Reducer } from "@umijs/max";
import { all } from "redux-saga/effects";
import { getLearningPfEnumConfig } from "@/api/user";
import type { LearningPfEnumConfigResult } from "@/api/user/types";

export interface LearningPfEnumModelState {
  enum_practice_grade: Array<{ label: string; value: string | number }>;
  enum_practice_difficulty: Array<{ label: string; value: string | number }>;
  enum_practice_subject: Array<{ label: string; value: string | number }>;
  enum_practice_primary: Array<{ label: string; value: string | number }>;
  enum_practice_junior: Array<{ label: string; value: string | number }>;
  enum_practice_high: Array<{ label: string; value: string | number }>;
  fetched: boolean;
  fetching: boolean;
}

export interface LearningPfEnumModelType {
  namespace: "learningPfEnum";
  state: LearningPfEnumModelState;
  effects: {
    fetchLearningPfEnums: Effect;
  };
  reducers: {
    saveLearningPfEnums: Reducer<LearningPfEnumModelState>;
    setFetching: Reducer<LearningPfEnumModelState>;
  };
}

const LearningPfEnumModel: LearningPfEnumModelType = {
  namespace: "learningPfEnum",
  state: {
    enum_practice_grade: [],
    enum_practice_difficulty: [],
    enum_practice_subject: [],
    enum_practice_primary: [],
    enum_practice_junior: [],
    enum_practice_high: [],
    fetched: false,
    fetching: false,
  },
  effects: {
    *fetchLearningPfEnums(_, { call, put, select }) {
      // 检查是否已获取或正在获取中
      const state: LearningPfEnumModelState = yield select(
        (s: { learningPfEnum: LearningPfEnumModelState }) => s?.learningPfEnum
      );
      if (state?.fetched || state?.fetching) {
        return;
      }

      // 设置 fetching 标志，防止并发请求
      yield put({
        type: "setFetching",
        payload: true,
      });

      try {
        // 并行请求三个枚举数据
        const [gradeResult, difficultyResult, subjectResult, primaryResult, juniorResult, highResult]: [
          LearningPfEnumConfigResult,
          LearningPfEnumConfigResult,
          LearningPfEnumConfigResult,
          LearningPfEnumConfigResult,
          LearningPfEnumConfigResult,
          LearningPfEnumConfigResult,
        ] = yield all([
          call(getLearningPfEnumConfig, { dictType: "enum_practice_grade" }),
          call(getLearningPfEnumConfig, {
            dictType: "enum_practice_difficulty",
          }),
          call(getLearningPfEnumConfig, { dictType: "enum_practice_subject" }),
          call(getLearningPfEnumConfig, { dictType: "enum_practice_primary" }),
          call(getLearningPfEnumConfig, { dictType: "enum_practice_junior" }),
          call(getLearningPfEnumConfig, { dictType: "enum_practice_high" }),
        ]);

        // 转换数据格式
        const formatData = (
          result: LearningPfEnumConfigResult
        ): Array<{ label: string; value: string | number }> => {
          if (result?.success && result.data) {
            return result.data.map((item) => ({
              label: item.enumName,
              value: item.enumValue,
            }));
          }
          return [];
        };

        yield put({
          type: "saveLearningPfEnums",
          payload: {
            enum_practice_grade: formatData(gradeResult),
            enum_practice_difficulty: formatData(difficultyResult),
            enum_practice_subject: formatData(subjectResult),
            enum_practice_primary: formatData(primaryResult),
            enum_practice_junior: formatData(juniorResult),
            enum_practice_high: formatData(highResult),
          },
        });
      } catch (error) {
        // 如果请求失败，重置 fetching 标志，允许重试
        yield put({
          type: "setFetching",
          payload: false,
        });
        throw error;
      }
    },
  },
  reducers: {
    saveLearningPfEnums(state, { payload }) {
      return {
        ...state,
        enum_practice_grade: payload.enum_practice_grade || [],
        enum_practice_difficulty: payload.enum_practice_difficulty || [],
        enum_practice_subject: payload.enum_practice_subject || [],
        enum_practice_primary: payload.enum_practice_primary || [],
        enum_practice_junior: payload.enum_practice_junior || [],
        enum_practice_high: payload.enum_practice_high || [],
        fetched: true,
        fetching: false,
      };
    },
    setFetching(state, { payload }) {
      return {
        ...state,
        fetching: payload,
      };
    },
  },
};

export default LearningPfEnumModel;

